/**
 * @fileoverview Virtual Race Product (race context, team dropdowns, medal)
 * @requires virtual-race-utility.js
 */

(function() {
    'use strict';

    if (typeof window.VirtualRaceUtils === 'undefined') {
        console.error('VirtualRaceUtils not loaded');
        return;
    }

    const { CONFIG, SecurityUtils, TeamColorManager, TeamLabelManager, EventHelpers, DOMHelpers, UIHelpers, UrlParams, StorageManager, BaseCustomElement, fetchDashboardTeamOptions } = window.VirtualRaceUtils;
    const LOCAL_CONFIG = {
        SECTION_TIMEOUT: 5000
    };

    const LocalValidators = {

        validateGraphQLResponse(response) {
            return !!(response && typeof response === 'object' && response.data);
        },

        validateTeamOption(option) {
            return !!(option && typeof option === 'object' && typeof option.key === 'string');
        }
    };
    if (!window.teamColorManager) {
        window.teamColorManager = new TeamColorManager();
    }

    class VirtualRaceProductFetcher {
        constructor() {
            this.productData = {};
            this.isteam = false;
            this.isLoading = false;
            this.error = null;
            this._loadingPromise = null;
            this._abortController = null;
            this._retryCount = 0;
        }

        async getProductData(forceRefresh = false) {
                const requestedId = this._getProductId();

                if (this._loadingPromise && !forceRefresh) {
                    return this._loadingPromise;
                }

                if (this.productData.prodId && !forceRefresh && requestedId === this.productData.prodId) {
                    return this.productData;
                }

                this._loadingPromise = this._fetchWithRetry()
                    .finally(() => {
                        this._loadingPromise = null;
                    });

                return this._loadingPromise;
        }

        /** Returns the same fetcher instance so multiple elements share one fetch. */
        static getShared() {
                if (!VirtualRaceProductFetcher._shared) {
                    VirtualRaceProductFetcher._shared = new VirtualRaceProductFetcher();
                }
                return VirtualRaceProductFetcher._shared;
        }

        async _fetchWithRetry() {
                this._retryCount = 0;

                while (this._retryCount < CONFIG.MAX_RETRIES) {
                    try {
                        return await this._fetchAndProcessProduct();
                    } catch (error) {
                        this._retryCount++;

                        if (this._retryCount >= CONFIG.MAX_RETRIES) {
                            throw error;
                        }

                        // Exponential backoff
                        await this._delay(CONFIG.RETRY_DELAY * Math.pow(2, this._retryCount - 1));
                    }
                }
        }

        _delay(ms) {
                return new Promise(resolve => setTimeout(resolve, ms));
        }

        async _fetchAndProcessProduct() {
                const rateLimit = SecurityUtils.checkRateLimit('productFetch');
                if (!rateLimit.allowed) {
                    throw new Error('RATE_LIMIT_EXCEEDED');
                }

                this.isLoading = true;
                this.error = null;

                try {
                    const productId = this._getProductId();
                    if (!productId) {
                        console.warn('No valid product selected');
                        return undefined;
                    }

                    const product = await this._fetchFromGraphQL(productId);
                    await this._processProductData(product);
                    return this.productData;
                } catch (error) {
                    this._handleError(error);
                    throw error;
                } finally {
                    this.isLoading = false;
                    this._abortController = null;
                }
        }

        _getProductId() {
                const fromUrl = UrlParams.getVrId();
                if (fromUrl) {
                    StorageManager.setSelectedProductId(fromUrl);
                    return fromUrl;
                }
                const stored = StorageManager.getSelectedProductId();
                return SecurityUtils.validateProductId(stored) || null;
        }

        async _fetchFromGraphQL(productId) {
                const query = this._buildGraphQLQuery(productId);

                this._abortController = new AbortController();
                const timeoutId = setTimeout(
                    () => this._abortController.abort(),
                    CONFIG.API_TIMEOUT
                );

                try {
                    const headers = {
                        'Content-Type': 'application/json',
                        'X-Shopify-Storefront-Access-Token': '382d0c774e95be1f0d7c90e6c08bba49'
                    };

                    const response = await fetch(
                        `${window.globalSpace.apiDomain}/api/2023-01/graphql.json`,
                        {
                            method: 'POST',
                            headers,
                            body: JSON.stringify({ query }),
                            signal: this._abortController.signal,
                            credentials: 'same-origin'
                        }
                    );

                    clearTimeout(timeoutId);

                    if (!response.ok) throw new Error('FETCH_FAILED');

                    const data = await response.json();

                    if (!LocalValidators.validateGraphQLResponse(data)) {
                        throw new Error('INVALID_RESPONSE');
                    }

                    if (!data.data?.product) {
                        throw new Error('PRODUCT_NOT_FOUND');
                    }
                

                    return data.data.product;


                } catch (error) {
                    clearTimeout(timeoutId);
                    throw error;
                }
        }

        _buildGraphQLQuery(productId) {
                return `
                    query getProduct {
                        product(id: "gid://shopify/Product/${productId}") {
                            title
                            tags
                            id
                            handle
                            variants(first: 1) {
                                nodes {
                                    metafields(identifiers: {namespace: "custom", key: "personalizations"}) {
                                        references(first: 1) {
                                            nodes {
                                                ... on Metaobject {
                                                    fields {
                                                        key
                                                        value
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            content: metafield(namespace: "custom", key: "virtual_race_content") {
                                reference {
                                    ... on Metaobject {
                                        raceRecap: field(key: "race_recap") { value }
                                        medalImage: field(key: "vr_listing_medal_image") {
                                            reference {
                                                ... on MediaImage {
                                                    image {
                                                        url
                                                        altText
                                                        width
                                                        height
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                `;
        }

        async _processProductData(product) {
                const productIdMatch = product.id.match(/\/Product\/(\d+)$/);
                if (!productIdMatch) throw new Error('INVALID_PRODUCT_ID');

                const productId = SecurityUtils.validateProductId(productIdMatch[1]);
                if (!productId) throw new Error('INVALID_PRODUCT_ID');

                this.productData.prodId = productId;

                await this._fetchSectionHTML(product.handle);
                this._parseTeamOptions(product);
                this._parseContentMetafield(product);

                if (this.isteam && this.productData.prodId) {
                    const dashboardTeams = await fetchDashboardTeamOptions(this.productData.prodId);
                    if (Array.isArray(dashboardTeams) && dashboardTeams.length > 0) {
                        this.productData.teamOptions = dashboardTeams;
                        if (window.teamColorManager && typeof window.teamColorManager.initializeFromTeams === 'function') {
                            window.teamColorManager.initializeFromTeams(dashboardTeams);
                        }
                    }
                }
        }

        async _fetchSectionHTML(handle) {
                this._abortController = new AbortController();
                const timeoutId = setTimeout(
                    () => this._abortController.abort(),
                    LOCAL_CONFIG.SECTION_TIMEOUT
                );
                try {
                    const sanitizedHandle = SecurityUtils.sanitizeText(handle);
                    const response = await fetch(
                        `/products/${encodeURIComponent(sanitizedHandle)}?sections=vr-fetch-metaobject`,
                        {
                            signal: this._abortController.signal,
                            credentials: 'same-origin'
                        }
                    );

                    if (response.ok) {
                        const sectionJson = await response.json();
                        if (sectionJson['vr-fetch-metaobject']) {
                            const sectionData = new DOMParser()
                                .parseFromString(sectionJson['vr-fetch-metaobject'], 'text/html')
                                .body;
                            this.productData.description = sectionData.innerHTML;
                        }
                    }
                } catch (e) {
                    console.warn('Section fetch failed:', e.name);
                } finally {
                    clearTimeout(timeoutId);
                }
        }

        _parseTeamOptions(product) {
                try {
                    const meta = product.variants?.nodes[0]?.metafields?.[0];
                    const optionsStr = meta?.references?.nodes?.[0]?.fields
                        ?.find(f => f.key === 'options')?.value;

                    if (!optionsStr) return;

                    const parsedOptions = JSON.parse(optionsStr);
                    if (!Array.isArray(parsedOptions)) return;

                    const validOptions = parsedOptions
                        .filter(opt => LocalValidators.validateTeamOption(opt))
                        .slice(0, CONFIG.MAX_TEAM_OPTIONS)
                        .map(opt => ({
                            key: SecurityUtils.sanitizeText(opt.key),
                            value: SecurityUtils.sanitizeText(opt.value || opt.key)
                        }));

                    this.productData.teamOptions = validOptions;
                    window.teamColorManager.initializeFromTeams(validOptions);

                } catch (e) {
                    console.error('Error parsing team options:', e);
                }
        }

        _parseContentMetafield(product) {
                if (!product.content?.reference) return;

                const metaobject = product.content.reference;

                this.productData.productTitle = SecurityUtils.sanitizeText(product.title);
                this.productData.handle = SecurityUtils.sanitizeText(product.handle);

                if (Array.isArray(product.tags)) {
                    this.checkTeam(product.tags);
                }

                if (metaobject.raceRecap?.value) {
                    this.productData.raceRecap = SecurityUtils.sanitizeText(metaobject.raceRecap.value);
                }

                if (metaobject.medalImage?.reference?.image) {
                    this._parseMedalImage(metaobject.medalImage.reference.image);
                }
        }

        _parseMedalImage(image) {
                const validatedUrl = SecurityUtils.validateUrl(image.url);
                if (validatedUrl) {
                    this.productData.medalImage = {
                        url: validatedUrl,
                        altText: SecurityUtils.sanitizeText(image.altText || 'Medal'),
                        width: parseInt(image.width) || 0,
                        height: parseInt(image.height) || 0
                    };
                }
        }

        checkTeam(tags) {
                const sanitizedTags = tags.map(tag =>
                    SecurityUtils.sanitizeText(tag).toLowerCase()
                );
                const hasTeam = sanitizedTags.some(tag => tag.includes('vrteam'));

                this.isteam = hasTeam;
                window.isVirtualRaceTeam = hasTeam;

                EventHelpers.dispatch('updateIsTeam', { isteam: hasTeam });

                document.querySelectorAll('[data-teamWrapper]').forEach(el => {
                    if (hasTeam) {
                        DOMHelpers.show(el);
                    } else {
                        el.remove();
                    }
                });
        }

        getTeamColor(teamName) {
                const color = window.teamColorManager.getColorForTeam(teamName);
                return SecurityUtils.validateColor(color);
        }

        validateTeamSelection(event) {
                const select = event.target;
                const alert = select.closest('.input-wrap')?.querySelector('[data-alert]');
                const value = SecurityUtils.sanitizeText(select.value);
                const hasError = value === 'N/A' || value === '';

                UIHelpers.setFieldError(select, alert, hasError);
        }

        _handleError(error) {
                const errorMessages = {
                    'RATE_LIMIT_EXCEEDED': 'Too many requests. Please wait a moment.',
                    'PRODUCT_NOT_FOUND': 'Product not found.',
                    'INVALID_PRODUCT_ID': 'Invalid product ID.',
                    'FETCH_FAILED': 'Unable to load product information.',
                    'INVALID_RESPONSE': 'Invalid server response.'
                };

                this.error = {
                    message: errorMessages[error.message] || 'Unable to load product information',
                    details: 'Please refresh the page and try again',
                    canRetry: error.name !== 'AbortError'
                };

                console.error('Product fetch error:', error.name);

                this._showErrorUI();

                if (window.gtag) {
                    gtag('event', 'exception', {
                        description: 'Product fetch failed',
                        fatal: false
                    });
                }
        }

        _showErrorUI() {
                EventHelpers.dispatch('productLoadError', this.error);

                const errorContainer = document.querySelector('[data-product-error]');
                if (!errorContainer) return;

                errorContainer.textContent = this.error.message;
                if (this.error.canRetry) {
                    const existingRetry = errorContainer.querySelector('button.btn-primary');
                    if (!existingRetry) {
                        const retryButton = document.createElement('button');
                        retryButton.textContent = 'Retry';
                        retryButton.className = 'btn btn-primary mt-2';
                        retryButton.addEventListener('click', () => {
                            this.getProductData(true);
                        });
                        errorContainer.appendChild(retryButton);
                    }
                }
                DOMHelpers.show(errorContainer);
        }

        destroy() {
            if (this._abortController) {
                this._abortController.abort();
                this._abortController = null;
            }
            this._loadingPromise = null;
        }
    }

    function fetchProduct() {
        return new VirtualRaceProductFetcher();
    }

    function getSharedProductFetcher() {
        return VirtualRaceProductFetcher.getShared();
    }

    window.fetchProduct = fetchProduct;
    window.getSharedProductFetcher = getSharedProductFetcher;

    /**
     *  Virtual Race Product Element
     */
    class VirtualRaceProductElement extends BaseCustomElement {

        async _init() {
            this._state = getSharedProductFetcher();
            await Promise.all([
                this._state.getProductData(),
                TeamLabelManager._ensureTeamsLoaded()
            ]);
            this._setupEventListeners();
        }

        disconnectedCallback() {
            this._eventListeners.forEach(({ element, event, handler, options }) => {
                element.removeEventListener(event, handler, options);
            });
            this._eventListeners = [];
            this._state = null;
            if (typeof this._cleanup === 'function') {
                this._cleanup();
            }
        }

        _setupEventListeners() {
            this._$$('[data-team-select]').forEach(teamSelect => {
                this._addEventListener(teamSelect, 'change', async (e) => {
                    if (!this._isInitializing && this._state) {
                        e.target.classList.add('disabled');
                        try {
                            await this._state.validateTeamSelection(e);
                            if (typeof this._state.filterTeamData === 'function') {
                                await this._state.filterTeamData(e.target.value);
                            }
                            if (!this._isEnterRaceTimePage()) {
                                DOMHelpers.applyTeamSelectColor(e.target, e.target.value || 'all', (key) => this._state.getTeamColor(key));
                            }
                        } finally {
                            e.target.classList.remove('disabled');
                        }
                    }
                });
            });
        }

        _isEnterRaceTimePage() {
            return window.location.pathname === '/pages/enter-your-virtual-race-time';
        }

        async _render() {
            if (!this._state || this._hasError) return;

            // Show content, hide loading
            this._toggleElements('[data-loading]', false);
            this._toggleElements('[data-content]', true);

            const pd = this._state.productData || {};
            
            
            const titleEl = this.querySelector('[data-product-title]');
            if (titleEl) titleEl.textContent = pd.productTitle || '';

            const titleInlineEl = this.querySelector('[data-product-title-inline]');
            if (titleInlineEl) titleInlineEl.textContent = pd.productTitle || '';

            const badgeEl = this.querySelector('[data-product-badge]');
            if (badgeEl) badgeEl.textContent = pd.raceRecap === 'Yes' ? 'Race Recap' : 'Ongoing Race';

            const descEl = this.querySelector('[data-product-description]');
            if (descEl && pd.description) {
                descEl.innerHTML = pd.description;
            }

            const medalImg = this.querySelector('[data-medal-image]');
            if (medalImg && pd.medalImage) {
                medalImg.src = pd.medalImage.url;
                medalImg.alt = pd.medalImage.altText || 'Medal';
                if (pd.medalImage.width) medalImg.width = pd.medalImage.width;
                if (pd.medalImage.height) medalImg.height = pd.medalImage.height;
                medalImg.hidden = false;
            } else if (medalImg) {
                medalImg.hidden = true;
            }

            const medalPlaceholder = this.querySelector('[data-medal-placeholder]');
            if (medalPlaceholder) {
                medalPlaceholder.hidden = !!(pd.medalImage && pd.medalImage.url);
            }

            const teamSelects = this.querySelectorAll('[data-team-select]');
            const options = await DOMHelpers.filterTeamOptionsForDropdown(pd.teamOptions);
            if (teamSelects.length && Array.isArray(pd.teamOptions)) {
                teamSelects.forEach(teamSelect => {
                    const value = teamSelect.value;
                    const isFilter = teamSelect.dataset.teamDefault === 'all';
                    const defaultValue = isFilter ? 'all' : 'N/A';
                    const defaultLabel = isFilter ? 'ALL TEAMS' : 'Select team';
                    DOMHelpers.populateTeamDropdown(teamSelect, options, {
                        defaultValue,
                        defaultLabel,
                        selectedValue: value || defaultValue,
                        getTeamColor: (teamKey) => this._state.getTeamColor(teamKey)
                    });
                    if (!this._isEnterRaceTimePage()) {
                        DOMHelpers.applyTeamSelectColor(teamSelect, teamSelect.value || defaultValue, (k) => this._state.getTeamColor(k));
                    }
                });
            }

            const recapLink = this.querySelector('[data-race-recap-link]');
            if (recapLink && pd.raceRecap !== 'Yes') {
                recapLink.href = `/products/${pd.handle || ''}`;
                recapLink.hidden = false;
            } else if (recapLink) {
                recapLink.hidden = true;
            }
            if(this.querySelector('[data-share-photos-link]')) {
                this.querySelector('[data-share-photos-link]')?.classList.remove('d-none');
            }

            const continueLink = this.querySelector('[data-continue-recap-link]');
            if (continueLink && pd.prodId) {
                continueLink.href = `/pages/virtual-race?vrId=${pd.prodId}`;
            }

            // Show filters after product load (hidden by default); team filter visibility is handled by data-teamWrapper
            const resultsFilters = this.querySelector('[data-results-filters]');
            if (resultsFilters) resultsFilters.classList.remove('d-none');
        }
    }

    if (!customElements.get('virtual-race-product')) {
        customElements.define('virtual-race-product', VirtualRaceProductElement);
    }

})();
